import Popper from 'popper.js';
import { ElementCreator } from './common/ElementCreator';
const tooltipHtml = `
<div class="tooltip">
  <div class="tooltip-arrow"></div>
  <div class="tooltip-inner"></div>
</div>
`;
const defaultTooltipOptions = {
    delayShow: 100,
    delayHide: 80,
    placement: 'bottom',
    clickableSelectors: [
        '.note',
        'mark',
        'em',
        'abbr',
        '[data-tooltip-clickable]'
    ]
};
class Tooltip {
    constructor(win, reference, container, opts) {
        this.win = win;
        this.reference = reference;
        this.container = container;
        this.opts = opts;
        this.isOpen = false;
        this.popperOptions = {
            placement: this.opts.placement
        };
    }
    show(event) {
        if (this.openedByEventType !== 'click') {
            this.openedByEventType = event.type;
        }
        if (this.hideTimeoutId) {
            this.win.clearTimeout(this.hideTimeoutId);
            this.hideTimeoutId = undefined;
            return;
        }
        if (this.isOpen || this.showTimeoutId)
            return;
        this.showTimeoutId = this.win.setTimeout(() => this.showAfterDelay(), this.opts.delayShow);
    }
    hide(event) {
        if (this.showTimeoutId) {
            this.win.clearTimeout(this.showTimeoutId);
            this.showTimeoutId = undefined;
            this.openedByEventType = undefined;
            return;
        }
        if (!this.isOpen || this.hideTimeoutId)
            return;
        if (!this.isValidHideEvent(event))
            return;
        this.hideTimeoutId = this.win.setTimeout(() => this.hideAfterDelay(), this.opts.delayHide);
    }
    isValidHideEvent(event) {
        if (!this.openedByEventType)
            return true;
        if (this.isEventOfReferenceElement(event)
            && ['mouseenter', 'focus'].includes(this.openedByEventType)
            && ['blur', 'mouseleave'].includes(event.type))
            return true;
        if (!this.isEventOfTooltipElement(event)
            && this.openedByEventType === 'click' && event.type === 'click')
            return true;
        return false;
    }
    isEventOfReferenceElement(event) {
        if (!(event.target instanceof HTMLElement))
            return false;
        return this.reference.contains(event.target);
    }
    isEventOfTooltipElement(event) {
        if (!this.tooltipElement)
            return false;
        if (!(event.target instanceof HTMLElement))
            return false;
        return this.tooltipElement.contains(event.target);
    }
    showAfterDelay() {
        this.showTimeoutId = undefined;
        if (this.isOpen)
            return;
        this.isOpen = true;
        if (!this.tooltipElement) {
            this.setupTooltipElement();
            this.popper = new Popper(this.reference, this.tooltipElement, this.popperOptions);
        }
        this.tooltipElement.classList.add('show');
        this.tooltipElement.setAttribute('aria-hidden', 'false');
        this.popper.update();
    }
    setupTooltipElement() {
        this.tooltipElement = ElementCreator.createFromHtml(tooltipHtml);
        const uid = Math.random()
            .toString(36)
            .substr(2, 10);
        this.tooltipElement.id = `tooltip_${uid}`;
        this.tooltipElement.setAttribute('aria-hidden', 'false');
        this.tooltipInnerElement = this.tooltipElement
            .getElementsByClassName('tooltip-inner')[0];
        this.tooltipInnerElement.innerHTML = this.reference.getAttribute('data-tooltip') || '';
        this.container.appendChild(this.tooltipElement);
    }
    hideAfterDelay() {
        this.hideTimeoutId = undefined;
        this.openedByEventType = undefined;
        this.isOpen = false;
        this.tooltipElement.setAttribute('aria-hidden', 'true');
        this.tooltipElement.classList.remove('show');
    }
}
export class Tooltipper {
    constructor(win = window, opts = {}) {
        this.win = win;
        this.options = Object.assign({}, opts, defaultTooltipOptions);
        this.doc = this.win.document;
    }
    activate() {
        Array.from(this.doc.querySelectorAll('[title]'))
            .forEach(element => this.convertTitleToTooltip(element));
        Array.from(this.doc.querySelectorAll('[data-tooltip]'))
            .forEach(element => this.activateTooltip(element));
    }
    convertTitleToTooltip(element) {
        const title = element.getAttribute('title');
        const tooltip = element.getAttribute('data-tooltip') || '';
        element.setAttribute('data-original-title', title || '');
        element.setAttribute('data-tooltip', title || tooltip || '');
        element.removeAttribute('title');
    }
    getOrCreateContainer() {
        if (this.tooltipContainer) {
            return this.tooltipContainer;
        }
        this.tooltipContainer = this.doc.createElement('div');
        this.tooltipContainer.id = 'tooltip-container';
        this.doc.body.append(this.tooltipContainer);
        return this.tooltipContainer;
    }
    activateTooltip(reference) {
        const tooltip = new Tooltip(this.win, reference, this.getOrCreateContainer(), this.options);
        this.registerEventListeners(reference, tooltip);
    }
    registerEventListeners(reference, tooltip) {
        const show = (event) => this.onShowEvent(event, reference, tooltip);
        const hide = (event) => this.onHideEvent(event, reference, tooltip);
        reference.addEventListener('mouseenter', show);
        reference.addEventListener('focus', show);
        reference.addEventListener('mouseleave', hide);
        reference.addEventListener('blur', hide);
        if (this.isClickable(reference)) {
            reference.addEventListener('click', show);
            this.doc.addEventListener('click', hide);
        }
    }
    isClickable(reference) {
        return this.options.clickableSelectors
            .some(selector => reference.matches(selector));
    }
    onShowEvent(event, element, tooltip) {
        event.stopPropagation();
        tooltip.show(event);
    }
    onHideEvent(event, element, tooltip) {
        event.stopPropagation();
        tooltip.hide(event);
    }
}
